/** Utilities for working with "framework agnostic" web components */

/** Defines common ways of selecting elements */
export type SelectorOptions = {
    tag?: string;
    class?: string;
    name?: string;
    required?: boolean;
};

/** 
 * Creates functions to select required/multiple elements within a parent container.
 * Returns an object with two functions:
 * - select: selects a single required element (throws if not found)
 * - selectAll: selects all matching elements (returns empty array if none found)
 * 
 * Supports both object syntax (tag, class, name) and raw selector strings.
 */
export function createQuerySelector(parent: HTMLElement) {
    const select = <T extends HTMLElement>(options: string | SelectorOptions): T => {
        const selector = resolveSelector(options);
        const element = parent.querySelector<T>(selector);
        if (!element) {
            throw new Error(`requires element matching ${selector}`);
        }
        return element;
    }

    const selectAll = <T extends HTMLElement>(options: string | SelectorOptions): T[] => {
        const selector = resolveSelector(options);
        return Array.from(parent.querySelectorAll<T>(selector));
    }

    return { select, selectAll };
}

function resolveSelector(options: string | SelectorOptions): string {
    return typeof options === 'string' ? options : formatSelector(options);
}

export function formatSelector({tag, class: className, name}: SelectorOptions): string {
    let selector = tag || '';
    if (className) {
        selector += `.${className}`;
    }
    if (name) {
        selector += `[name="${name}"]`;
    }
    return selector;
}

export function select<T extends HTMLElement>(parent: HTMLElement, options: SelectorOptions): T {
    const selector = formatSelector(options);
    const element = parent.querySelector<T>(selector);
    if (!element && options.required) {
        throw new Error(`requires element matching ${selector}`);
    }
    return element;
}

/** Shortcut for the common operation of selecting a button by name */
export function selectButton(parent: HTMLElement, name: string, required = false): HTMLButtonElement {
    return select<HTMLButtonElement>(parent, { tag: 'button', name, required });
}

/** Shortcut for the common operation of selecting a form by name */
export function selectForm(parent: HTMLElement, name: string, required = false): HTMLFormElement {
    return select<HTMLFormElement>(parent, { tag: 'form', name, required });
}

/** Used internally to keep track of active event listeners */
class ActiveEventListener {
    constructor(
        private element: HTMLElement,
        private type: string,
        private listener: EventListenerOrEventListenerObject,
    ) {}

    remove() {
        this.element.removeEventListener(this.type, this.listener);
    }
}

type ElementOrList = HTMLElement | HTMLElement[] | NodeList;

/** Shorthand for event listener options */
export type EventListenerOptions = [ElementOrList, string, EventListenerOrEventListenerObject];

/**
 * Adds and keeps track of active event listeners, allowing for easy cleanup.
 * For example, in the lifecycle of a web component, event listeners would typically 
 * be added in `connectedCallback` and removed in the `disconnectedCallback`.
 */
export class EventManager {
    private activeListeners: ActiveEventListener[] = [];

    withEventListeners(listeners: EventListenerOptions[]): EventManager {
        listeners.forEach(([element, type, listener]) => {
            this.addEventListener(element, type, listener);
        });
        return this;
    }

    addEventListener(element: ElementOrList, type: string, listener: EventListenerOrEventListenerObject) {
        if (element instanceof HTMLElement) {
            element.addEventListener(type, listener);
            this.activeListeners.push(new ActiveEventListener(element, type, listener));
        } else{
            element.forEach((el: HTMLElement) => this.addEventListener(el, type, listener));
        }
    }

    removeEventListeners() {
        this.activeListeners.forEach(listener => listener.remove());
        this.activeListeners = [];
    }
}